import { Search, X, SlidersHorizontal, RotateCcw, Filter, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select.jsx';
import { SHOP_CATEGORIES } from '../../../lib/shop-taxonomy.js';

export function TopbarFilterLayout({
  categories = SHOP_CATEGORIES,
  activeCategory = '',
  activeSubcategory = '',
  onSelectCategory,
  onSelectSubcategory,
  search = '',
  onSearchChange,
  onSearchSubmit,
  manufacturers = [],
  selectedManufacturer = '',
  onSelectManufacturer,
  sort = 'recommended',
  onSortChange,
  onResetFilters,
}) {
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (onSearchSubmit) onSearchSubmit(e);
  };

  const activeCategoryObj = (categories || SHOP_CATEGORIES).find(
    (c) => c.slug === activeCategory
  );
  const subcategories = activeCategoryObj?.subcategories || [];

  const hasActiveFilters = Boolean(
    activeCategory || activeSubcategory || selectedManufacturer || (search && search.trim())
  );

  return (
    <div
      data-filter-variant="topbar"
      className="sticky top-16 z-30 w-full rounded-2xl border border-[var(--border)] bg-white/95 p-3.5 shadow-sm backdrop-blur-md transition-all space-y-3"
    >
      {/* Primary Toolbar Row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        
        {/* Search & Category Group */}
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[280px]">
          
          {/* Quick Search */}
          <form onSubmit={handleFormSubmit} className="relative w-full sm:w-64">
            <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="SKU oder Begriff..."
              value={search}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              className="h-9 w-full rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] pl-8 pr-7 text-xs text-[var(--foreground)] placeholder:text-muted-foreground focus:bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--primary)]"
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearchChange && onSearchChange('')}
                className="absolute right-2 top-2.5 text-muted-foreground hover:text-[var(--foreground)] cursor-pointer"
                aria-label="Suchtext löschen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>

          {/* Category Dropdown */}
          <div className="w-48 sm:w-56">
            <Select
              value={activeCategory || 'all'}
              onValueChange={(val) => onSelectCategory && onSelectCategory(val === 'all' ? '' : val)}
            >
              <SelectTrigger className="h-9 text-xs rounded-xl">
                <SelectValue placeholder="Fachbereich wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Fachbereiche</SelectItem>
                {(categories || SHOP_CATEGORIES).map((cat) => (
                  <SelectItem key={cat.slug} value={cat.slug}>
                    {cat.label || cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subcategory Dropdown (if category has subcategories) */}
          {subcategories.length > 0 && (
            <div className="w-44 sm:w-48">
              <Select
                value={activeSubcategory || 'all'}
                onValueChange={(val) => onSelectSubcategory && onSelectSubcategory(val === 'all' ? '' : val)}
              >
                <SelectTrigger className="h-9 text-xs rounded-xl">
                  <SelectValue placeholder="Unterkategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Unterkategorien</SelectItem>
                  {subcategories.map((sub) => (
                    <SelectItem key={sub.slug} value={sub.slug}>
                      {sub.label || sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Manufacturer Dropdown */}
          {manufacturers.length > 0 && (
            <div className="w-36 sm:w-40">
              <Select
                value={selectedManufacturer || 'all'}
                onValueChange={(val) => onSelectManufacturer && onSelectManufacturer(val === 'all' ? '' : val)}
              >
                <SelectTrigger className="h-9 text-xs rounded-xl">
                  <SelectValue placeholder="Hersteller" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Hersteller</SelectItem>
                  {manufacturers.map((mf) => (
                    <SelectItem key={mf} value={mf}>
                      {mf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

        </div>

        {/* Sort & Reset Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {onSortChange && (
            <div className="w-40 sm:w-44">
              <Select value={sort} onValueChange={onSortChange}>
                <SelectTrigger className="h-9 text-xs rounded-xl">
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recommended">Empfehlung</SelectItem>
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  <SelectItem value="price_asc">Preis (aufsteigend)</SelectItem>
                  <SelectItem value="price_desc">Preis (absteigend)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {hasActiveFilters && onResetFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onResetFilters}
              className="h-9 px-3 text-xs gap-1 rounded-xl text-[var(--secondary)] hover:text-[var(--foreground)] cursor-pointer"
              title="Alle Filter zurücksetzen"
            >
              <RotateCcw className="h-3 w-3" />
              <span className="hidden sm:inline">Reset</span>
            </Button>
          )}
        </div>

      </div>

      {/* Active Filter Chips Row */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--border)] pt-2 text-xs">
          <span className="text-xs font-semibold text-[var(--secondary)] mr-1">Aktive Filter:</span>
          
          {activeCategory && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2.5 py-0.5 text-xs font-bold text-[var(--primary)]">
              <span>{activeCategoryObj?.label || activeCategoryObj?.name || activeCategory}</span>
              <button
                type="button"
                onClick={() => onSelectCategory && onSelectCategory('')}
                className="hover:text-[var(--foreground)] cursor-pointer"
                aria-label="Kategorie-Filter entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          {activeSubcategory && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2.5 py-0.5 text-xs font-bold text-[var(--primary)]">
              <span>{activeSubcategory}</span>
              <button
                type="button"
                onClick={() => onSelectSubcategory && onSelectSubcategory('')}
                className="hover:text-[var(--foreground)] cursor-pointer"
                aria-label="Unterkategorie-Filter entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          {selectedManufacturer && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-container-high,#f1f5f9)] border border-[var(--border)] px-2.5 py-0.5 text-xs font-medium text-[var(--foreground)]">
              <span>{selectedManufacturer}</span>
              <button
                type="button"
                onClick={() => onSelectManufacturer && onSelectManufacturer('')}
                className="hover:text-[var(--primary)] cursor-pointer"
                aria-label="Hersteller-Filter entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          {search && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-container-high,#f1f5f9)] border border-[var(--border)] px-2.5 py-0.5 text-xs font-medium text-[var(--foreground)]">
              <span>"{search}"</span>
              <button
                type="button"
                onClick={() => onSearchChange && onSearchChange('')}
                className="hover:text-[var(--primary)] cursor-pointer"
                aria-label="Suchfilter entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
